import Vue from 'vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import QuadrantTooltip from 'components/DataWidgets/QuadrantWidget/QuadrantTooltip.vue';
import QuadrantChart from 'components/charts/QuadrantChart/QuadrantChart.vue';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import ToggleCheckbox from 'components/widgets/ToggleCheckbox.vue';
import icon from 'assets/img/dashboards/dash-quadrant.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import { formatNPS, decimalAsPercent, number } from 'src/utils/formatters';
import ProjectAPI from 'src/api/project';
import { minMax, dotColor, quadrantColors, menus, baseRequirements, rowsFromPayload, overallRow, rowsToCsv, Limit, } from './QuadrantWidget.utils';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
const QuadrantWidget = Vue.extend({
    components: {
        WidgetFrame,
        WidgetMenu,
        QuadrantChart,
        QuadrantTooltip,
        ToggleCheckbox,
        DownloadExportButton,
        WidgetMessagePanel,
    },
    inject: {
        featureFlags: {
            from: 'featureFlags',
            default: () => ({}),
        }
    },
    props: {
        /** for export filename */
        exportName: { type: String, required: false, default: '' },
        /** is this DataWidget in zoomed mode?  */
        isZoomed: { type: Boolean, required: false, default: false },
        /** route object for zoom button */
        zoomToRoute: { type: Object, required: false, default: null },
        /** data to render  */
        data: { type: Object, required: false, default: null },
        /** show developer tools in widget */
        devMode: { type: Boolean, required: false, default: false },
        /** list of queries for the chart */
        queries: { type: Array, required: false, default: () => [] },
        /** fields for menu */
        segmentFields: { type: Array, required: false, default: () => [] },
        sortedSegmentsPerField: { type: Object, required: false, default: () => null },
        /** does this data contain NPS? */
        hasNps: { type: Boolean, required: false, default: false },
        /** does this data contain sentiment? */
        hasSentiment: { type: Boolean, required: false, default: false },
        /** does this data contain numeric fields? */
        hasNumericFields: { type: Boolean, default: false, required: false },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        /** list of concepts queries for the chart */
        concepts: { type: Array, required: false, default: () => [] },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        /** route to navigate to when theme/concept is clicked on */
        toQueryRoute: { type: Object, required: false, default: null },
        toConceptRoute: { type: Object, required: false, default: null },
        themeGroups: { type: Array, required: false, default: () => [] },
    },
    data() {
        return {
            icon,
            errorIcon,
            width: 300,
            height: 300,
            NUMERIC: 'Numerical Field',
            NPS: 'NPS',
            SENTIMENT: 'Sentiment',
            hoveredDot: null,
            hidden: [],
            // Default menu options
            selectedData: '',
            selectedDisplay: '',
            limitY: Limit.SELECTION,
            limitX: Limit.SELECTION,
        };
    },
    computed: {
        defaultSelectedData() {
            return this.hasQueries ? 'Themes' : 'Top Concepts';
        },
        hasQueries() {
            return this.queries?.length > 0;
        },
        isQueryView() {
            return ['Themes', 'Top Concepts', 'Theme Groups'].includes(this.selectedData);
        },
        displayType() {
            switch (this.selectedDisplay) {
                case this.NpsField:
                case 'Detractors':
                case 'Promoters':
                case 'Passives':
                    return this.NPS;
                case 'Positive Sentiment':
                case 'Negative Sentiment':
                case 'Neutral Sentiment':
                case 'Mixed Sentiment':
                    return this.SENTIMENT;
                default:
                    return this.NUMERIC;
            }
        },
        quadrantColors() {
            return quadrantColors(this.selectedDisplay, this.NpsField);
        },
        selectedDataType() {
            let type = null;
            switch (this.selectedData) {
                case 'Themes':
                    type = 'theme';
                    break;
                case 'Theme Groups':
                    type = 'group';
                    break;
                case 'Top Concepts':
                    type = 'concept';
                    break;
                default:
                    type = 'segment';
                    break;
            }
            return type;
        },
        menus() {
            return menus(this.selectedDisplay, this.selectedData, this.sortedSegmentsPerField, this.hasNps, this.hasSentiment, this.hasNumericFields, this.NpsField, this.numericalFields, this.isZoomed, this.limitX, this.hasQueries, this.featureFlags);
        },
        numericalFields() {
            if (!this.segmentFields)
                return [];
            return this.segmentFields.reduce((p, c) => c.type === ProjectAPI.COLUMN_LABELED_TYPES.get('NUMBER') ? p.concat(c.name) : p, []);
        },
        userErrors() {
            if (this.isQueryView) {
                return this.data?.queries?.userError;
            }
            else {
                return this.data?.queries?.userError || this.data?.fields?.userError;
            }
        },
        hasErrored() {
            // if we are showing queries then we don't check the fields props
            if (this.isQueryView) {
                return !!this.data?.queries?.error;
            }
            else {
                return !!this.data?.queries?.error || !!this.data?.fields?.error;
            }
        },
        isLoading() {
            if (!this.validatedOptions || !this.selectedData)
                return true;
            // if we are showing queries then we don't check the fields props
            if (this.isQueryView) {
                if (!this.data?.queries)
                    return true;
                return this.data.queries.status === 'fetching';
            }
            else {
                if (this.data?.queries && this.data?.fields) {
                    return this.data.queries.status === 'fetching' || this.data.fields.status === 'fetching';
                }
                else
                    return true;
            }
        },
        NpsField() {
            if (!this.hasNps)
                return null;
            const field = this.segmentFields.find(f => f.type === ProjectAPI.COLUMN_LABELED_TYPES.get('NPS'));
            return field ? field.name : null;
        },
        validatedOptions() {
            const NpsField = this.NpsField;
            const numericalFields = this.numericalFields;
            let display = this.config?.options?.display;
            let data = this.config?.options?.data;
            const validDisplayValues = [
                NpsField, 'Detractors', 'Promoters', 'Passives',
                'Positive Sentiment', 'Negative Sentiment', 'Neutral Sentiment', 'Mixed Sentiment',
            ].concat(this.numericalFields);
            if (!display ||
                !data ||
                !validDisplayValues.includes(display) ||
                (!this.hasNps && [NpsField, 'Detractors', 'Promoters', 'Passives'].includes(display)) ||
                (!this.hasSentiment && display.endsWith('Sentiment')) ||
                (!this.hasNumericFields && numericalFields.includes(display))) {
                if (this.hasNumericFields) {
                    display = this.numericalFields[0];
                }
                if (this.hasSentiment) {
                    display = 'Positive Sentiment';
                }
                if (this.hasNps) {
                    display = this.NpsField;
                }
                data = this.defaultSelectedData;
            }
            return { display, data };
        },
        /**
         * min & max values for Y axis rounding
         */
        yMinMax() {
            const padding = 0;
            if (!this.visibleDots)
                return { min: 0, max: 100 };
            let dots = [];
            switch (this.limitY) {
                case Limit.FULL:
                    return { min: 0, max: 100 };
                case Limit.SELECTION:
                    dots = this.visibleDots.filter(dot => dot.label !== 'overall__' && !dot.hidden).map(dot => dot.y);
                    break;
                case Limit.DATA:
                default:
                    dots = this.visibleDots.filter(dot => dot.label !== 'overall__').map(dot => dot.y);
                    break;
            }
            // if we are in selection mode & have unselected everything return FULL range
            if (dots.length === 0 && this.limitY === Limit.SELECTION)
                return { min: 0, max: 100 };
            let { max, min } = minMax(dots, padding);
            // minimum height so we don't end up in a weird visual
            if (max === 0) {
                max += 3;
            }
            return {
                max,
                min
            };
        },
        /**
         * min & max values for X axis
         */
        xMinMax() {
            const padding = 0;
            const isNPS = this.selectedDisplay === this.NpsField;
            if (!this.visibleDots)
                return isNPS ? { min: -100, max: 100 } : { min: 0, max: 100 };
            let dots = [];
            switch (this.limitX) {
                case Limit.SELECTION:
                    dots = this.visibleDots.filter(dot => dot.label !== 'overall__' && !dot.hidden).map(dot => dot.x);
                    break;
                case Limit.FULL:
                case Limit.DATA:
                default:
                    dots = this.visibleDots.filter(dot => dot.label !== 'overall__').map(dot => dot.x);
                    break;
            }
            // if we are in selection mode & have unselected everything:
            //  - return FULL range for sentiment & NPS
            //  - otherwise include all dots (with overall) to determine full range to return
            if (dots.length === 0 && this.limitX === Limit.SELECTION) {
                if ((this.displayType === this.SENTIMENT) || (!isNPS && this.displayType === this.NPS))
                    return { min: 0, max: 100 };
                if (isNPS)
                    return { min: -100, max: 100 };
                dots = this.visibleDots.map(dot => dot.x);
            }
            if (this.limitX === Limit.FULL && this.displayType === this.SENTIMENT)
                return { min: 0, max: 100 };
            const result = minMax(dots, padding);
            let { min, max } = result;
            // min width to avoid wierd visual
            if (min === max) {
                max += 3;
            }
            // if showing NPS & no range limit, then -100 to 100
            if (isNPS && this.limitX === Limit.FULL) {
                min = -100;
                max = 100;
            }
            // if showing NPS (with limit) ensure -100 to 100
            if (isNPS && this.limitX !== Limit.FULL) {
                min = min < -100 ? -100 : min;
                max = max > 100 ? 100 : max;
            }
            return { min, max };
        },
        // returns array of objects for the vertical overall bar on the chart
        // we one have one, the overall, but the chart supports multiple,
        // hence it has to be an array
        vert() {
            if (!this.selectedDisplay || !this.dataPoints)
                return [];
            if (!this.yMinMax && !this.yMinMax.max)
                return [];
            const overall = this.overallRow;
            if (!overall)
                return [];
            let value = 0;
            let label = '';
            let color = "#000";
            switch (this.selectedDisplay) {
                case this.NpsField:
                    value = overall['NPS Category|nps__'];
                    label = `Overall NPS (${formatNPS(value)})`;
                    break;
                case 'Detractors':
                case 'Promoters':
                case 'Passives':
                    value = overall[`NPS Category|${this.selectedDisplay.slice(0, -1)}%__`];
                    label = `Overall ${this.selectedDisplay} (${decimalAsPercent(value / 100)})`;
                    break;
                case 'Positive Sentiment':
                case 'Negative Sentiment':
                case 'Neutral Sentiment':
                case 'Mixed Sentiment':
                    const sentimentKey = `sentiment__|${this.selectedDisplay.split(' ')[0].toLowerCase()}\%__`;
                    value = overall[sentimentKey];
                    label = `Overall ${this.selectedDisplay} (${decimalAsPercent(value / 100)})`;
                    break;
                default:
                    value = overall[`${this.selectedDisplay}|mean__`];
                    label = `Overall ${this.selectedDisplay} (${number(value, '0.00')})`;
            }
            if (this.yMinMax && this.yMinMax.max) {
                color = dotColor(0, this.yMinMax.max, this.selectedDisplay, this.NpsField, this.yMinMax.max, this.xMinMax.max);
            }
            return [{
                    id: label,
                    label,
                    value,
                    color,
                    showLine: value >= this.xMinMax.min && value <= this.xMinMax.max
                }];
        },
        yAxis() {
            let min = 0;
            let max = 100;
            if (this.yMinMax) {
                max = this.yMinMax.max;
                min = this.yMinMax.min;
            }
            return {
                label: 'Relative Frequency',
                min,
                max,
                percent: true,
                markers: [
                    {
                        label: `${min}%`,
                        value: min
                    },
                    {
                        label: `${max}%`,
                        value: max
                    }
                ]
            };
        },
        xAxis() {
            let min = 0;
            let max = 100;
            if (this.xMinMax) {
                min = this.xMinMax.min;
                max = this.xMinMax.max;
            }
            const percent = !this.numericalFields.concat(this.NpsField).includes(this.selectedDisplay);
            return {
                label: this.selectedDisplay,
                min: min,
                max: max,
                percent,
                markers: [
                    {
                        label: `${min}${percent ? '%' : ''}`,
                        value: min
                    },
                    {
                        label: `${max}${percent ? '%' : ''}`,
                        value: max
                    }
                ]
            };
        },
        dataPoints() {
            if (!this.data)
                return [];
            return rowsFromPayload(this.data?.queries?.data?.payload, this.data?.fields?.data?.payload, this.selectedData);
        },
        overallRow() {
            if (!this.data)
                return null;
            return overallRow(this.data?.queries?.data?.payload);
        },
        /**
         * returns array of
         * {
              id: string,
              x: number,
              y: number,
              label: string,
              color: hex color code
              size: number
            }
         */
        dots() {
            if (this.isLoading || !this.dataPoints || !this.selectedData)
                return [];
            return this.dataPoints
                .filter((dot) => (dot.group__ !== 'overall__' && this.selectedData && !this.isQueryView) ||
                (dot.label !== '(No Value)' && dot.records !== 0) // if no value, then freq must be greater than zero
            )
                .map((point, index) => {
                let dot = {
                    id: `${point.label}_${index}`,
                    label: point.label,
                    x: null,
                    y: point.frequency * 100,
                    color: null,
                    size: 5,
                };
                switch (this.selectedDisplay) {
                    case this.NpsField:
                        dot.x = point['NPS Category|nps__'];
                        break;
                    case 'Detractors':
                    case 'Promoters':
                    case 'Passives':
                        dot.x = point[`NPS Category|${this.selectedDisplay.slice(0, -1)}%__`];
                        break;
                    case 'Positive Sentiment':
                    case 'Negative Sentiment':
                    case 'Neutral Sentiment':
                    case 'Mixed Sentiment':
                        dot.x = point[`sentiment__|${this.selectedDisplay.split(' ')[0].toLowerCase()}%__`];
                        break;
                    default:
                        // if we got down here then it's numerics
                        dot.x = point[`${this.selectedDisplay}|mean__`];
                }
                return dot;
            })
                .filter((d) => d.x !== null && d.x !== undefined);
        },
        // we set the color here because it requires the max Y value to have been already been calculated
        // and doing it in visible dots causes an infinite depth self-reference error
        colouredVisibleDots() {
            return this.visibleDots.map(dot => ({
                ...dot,
                color: dotColor(dot.x, dot.y, this.selectedDisplay, this.NpsField, this.yMinMax, this.xMinMax),
            }));
        },
        visibleDots() {
            return this.dots
                .filter(dot => dot.label !== 'overall__') // we exclude this here instead of in dots() so the minMax can take it into account
                .map((dot, index) => ({
                ...dot,
                hidden: this.hidden.includes(dot.label),
                hovered: index === this.hoveredDot
            }));
        },
        toolTip() {
            if (this.hoveredDot === null)
                return null;
            const name = this.visibleDots[this.hoveredDot].label;
            const dot = this.dataPoints.find(dp => dp.label === name);
            if (!dot)
                return null;
            let toolTip = {
                label: dot.label,
                records: dot.records,
                frequency: dot.frequency
            };
            if (this.hasNps && this.displayType === this.NPS) {
                toolTip = {
                    ...toolTip,
                    nps: dot['NPS Category|nps__'],
                    promoters: dot['NPS Category|Promoter%__'] / 100,
                    detractors: dot['NPS Category|Detractor%__'] / 100,
                    passives: dot['NPS Category|Passive%__'] / 100,
                };
            }
            if (this.hasSentiment && this.displayType === this.SENTIMENT) {
                toolTip = {
                    ...toolTip,
                    positive: dot['sentiment__|positive%__'] / 100,
                    negative: dot['sentiment__|negative%__'] / 100,
                    mixed: dot['sentiment__|mixed%__'] / 100,
                    neutral: dot['sentiment__|neutral%__'] / 100
                };
            }
            if (this.hasNumericFields && this.displayType === this.NUMERIC) {
                toolTip = {
                    ...toolTip,
                    aggregationLabel: this.selectedDisplay,
                    aggregationValue: dot[`${this.selectedDisplay}|mean__`]
                };
            }
            return toolTip;
        }
    },
    watch: {
        config: {
            deep: true,
            handler() {
                this.setOptionsFromConfig();
            }
        },
        queries: {
            deep: true,
            handler() {
                this.fetchData();
            }
        }
    },
    mounted() {
        this.setOptionsFromConfig();
        this.fetchData();
    },
    methods: {
        navigateTo(value) {
            if (this.selectedDataType === 'segment') {
                this.$emit('segment-clicked', this.selectedData, value);
            }
            else {
                switch (this.selectedDataType) {
                    case 'concept':
                        this.$emit('go-to-concept', value);
                        break;
                    case 'theme':
                        this.$emit('go-to-theme', value);
                        break;
                }
            }
        },
        updateConfig() {
            const updated = Object.assign({}, this.config, {
                options: {
                    display: this.selectedDisplay,
                    data: this.selectedData,
                    limitX: this.limitX,
                    limitY: this.limitY,
                }
            });
            this.$emit('config-changed', updated);
        },
        setOptionsFromConfig() {
            const validOptions = this.validatedOptions;
            this.selectedDisplay = validOptions.display;
            this.selectedData = validOptions.data;
            this.limitX = this.config?.options?.limitX ?? Limit.SELECTION;
            this.limitY = this.config?.options?.limitY ?? Limit.SELECTION;
        },
        mouseOver(index) {
            if (this.visibleDots[index] && this.visibleDots[index].hidden)
                return;
            this.setToolTip(index);
        },
        mouseOut() {
            this.setToolTip(null);
        },
        setToolTip(index) {
            this.hoveredDot = index === null || index === undefined ? null : index;
        },
        setSelection(menu, [title, item], fromConfig = false) {
            switch (menu) {
                case 'Zoom to':
                    this.limitX = item;
                    this.limitY = item;
                    break;
                case 'Display':
                    if (!fromConfig) { // don't track programmatic setting of this option
                        this.$analytics.track.quadrantChart.changeDisplay(title, item);
                    }
                    if (item === this.NpsField) {
                        this.selectedDisplay = this.NpsField;
                        break;
                    }
                    this.selectedDisplay = item;
                    break;
                case 'Data':
                    this.selectedData = item;
                    if (!fromConfig) { // don't track programmatic setting of this option
                        this.$analytics.track.quadrantChart.changeField(title, item);
                    }
                    break;
            }
            if (!fromConfig) {
                this.updateConfig();
            }
            this.fetchData();
        },
        fetchData(force = false) {
            if (!this.selectedData)
                return;
            /** request for segmentation chart overall data
             * @event requires
             * @property {string} id identifies this component request uniquely
             * @property {object} request chrysalis request
             * @property {boolean} force override cache
             * @property {boolean} multi use v2 api
             */
            const getPivotQueries = () => {
                if (this.selectedData === 'Themes') {
                    return this.queries;
                }
                else if (this.selectedData === 'Theme Groups') {
                    return this.themeGroups;
                }
                else {
                    return this.concepts;
                }
            };
            let requirements = baseRequirements(this.hasNps, this.hasSentiment, this.displayType === this.NUMERIC ? [this.selectedDisplay || ''] : null, this.selectedData, getPivotQueries(), ['Themes', 'Theme Groups'].includes(this.selectedData) ? 0 : 25);
            this.$emit('requires', 'quadrant__queries', requirements.queries, force);
            if (!this.isQueryView) {
                this.$emit('requires', 'quadrant__fields', requirements.fields, force);
            }
        },
        refresh() {
            window.location.reload();
        },
        reload() {
            this.fetchData(true);
        },
        contact() {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        },
        togglePoint(index) {
            if (!this.hidden.includes(this.visibleDots[index].label)) {
                this.hidden.push(this.visibleDots[index].label);
                this.hoveredDot = null;
            }
            else {
                // Remove the label with a filter.
                this.hidden = this.hidden.filter(x => x !== this.visibleDots[index].label);
                this.hoveredDot = index;
            }
        },
        selectAll() {
            this.hidden = [];
        },
        deselectAll() {
            this.hidden = this.dots.map(d => d.label);
        },
        setChartDimensions(width, height) {
            this.width = width;
            this.height = height;
        },
        // ================================= EXPORTS
        getCsvData() {
            return rowsToCsv(this.dataPoints, this.displayType, this.selectedData, this.selectedDisplay, this.visibleDots);
        },
        getChartEl() {
            return this.$el.querySelector('div.content');
        },
        getSvgExportConfig() {
            return { dims: { height: this.height, width: this.width }, css: `.text {
            font-weight: bold;
            color: black;
          }
          .point {
            stroke-opacity: 0.6;
            fill-opacity: 1;
            stroke-width: 1.5;
          }
          .hidden {
            display: none;
            cursor: none;
            fill-opacity: 0;
            stroke-opacity: 0;
          }
          .axis-label {
            font-weight: bold;
          }
          line.baseline-line {
            stroke-width: 2;
            stroke-opacity: 0.25;
          }
          text.baseline-line {
            font-weight: bold;
          }
    ` };
        },
        async makePptSlide(pptx) {
            const slide = pptx.addSlide();
            slide.addImage({
                data: await this.$refs.exportButton.generateImageData('image/png'),
                sizing: {
                    type: 'contain',
                    x: '0%',
                    y: '0%',
                    w: '100%',
                    h: '100%',
                }
            });
        },
    }
});
export default QuadrantWidget;
